// lib/appbar/appbar.tsx
import { isMobile } from "@lune/common/device";

// lib/appbar/desktop.tsx
import styled3 from "@emotion/styled";
import { UserInfo } from "@lune/auth/user";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import {
  IconButton,
  AppBar as MuiAppBar,
  Toolbar as MuiToolbar,
  toolbarClasses
} from "@mui/material";

// lib/title/title.tsx
import styled from "@emotion/styled";
import { Typography, useTheme } from "@mui/material";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var TitleSection = styled.div(
  ({ theme }) => `
  display: flex;
  flex: 1;
  gap: 1rem;
  align-items: center;
  filter: ${theme.palette.mode === "dark" ? "grayscale(1)" : "none"};
`
);
var LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-items: center;
  gap: 0.5rem;

  & img {
    max-width: 64px;
    max-height: 64px;
  }
`;
var Name = styled(Typography)`
  display: flex;
  font-family: monospace;
  font-weight: 700;
  letter-spacing: 0.3rem;
  text-decoration: none;
  align-items: center;
  justify-items: center;
`;
var Title = ({
  logo,
  name,
  versionLogo
}) => {
  const theme = useTheme();
  return /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsxs(TitleSection, { theme, children: [
    logo !== void 0 && /* @__PURE__ */ jsx(LogoContainer, { children: logo }),
    name !== void 0 && /* @__PURE__ */ jsxs(Name, { variant: "h6", noWrap: true, children: [
      name,
      versionLogo !== void 0 && /* @__PURE__ */ jsx(LogoContainer, { children: versionLogo })
    ] })
  ] }) });
};

// lib/appbar/searchAppBar.tsx
import styled2 from "@emotion/styled";
import { logDebug } from "@lune/common/logger";
import { Search as SearchIcon } from "@mui/icons-material";
import { InputBase, alpha, useTheme as useTheme2 } from "@mui/material";
import hotkeys from "hotkeys-js";
import { useEffect, useRef, useState } from "react";
import { Fragment as Fragment2, jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var DEBOUNCE_WAIT_MS = 1 * 1e3;
var Search = styled2.div(() => {
  const theme = useTheme2();
  return {
    position: "relative",
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    "&:hover": {
      backgroundColor: alpha(theme.palette.common.white, 0.25)
    },
    marginLeft: 0,
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      marginLeft: theme.spacing(1),
      width: "auto"
    }
  };
});
var SearchIconWrapper = styled2("div")(() => {
  const theme = useTheme2();
  return {
    padding: theme.spacing(0, 2),
    height: "100%",
    position: "absolute",
    pointerEvents: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  };
});
var StyledTextField = styled2(InputBase)(() => {
  const theme = useTheme2();
  return () => ({
    color: "inherit",
    "& .MuiInputBase-input": {
      padding: theme.spacing(1, 1, 1, 0),
      // vertical padding + font size from searchIcon
      paddingLeft: `calc(1em + ${theme.spacing(4)})`,
      transition: theme.transitions.create("width"),
      width: "100%",
      [theme.breakpoints.up("sm")]: {
        width: "12ch",
        "&:focus": {
          width: "20ch"
        }
      }
    }
  });
});
var SearchAppBar = ({
  search,
  onSearch,
  initialValue = "",
  sx,
  debounceMs = DEBOUNCE_WAIT_MS
}) => {
  const ref = useRef(null);
  const [text, setText] = useState(initialValue);
  const [debouncedValue, setDebouncedText] = useState(
    initialValue
  );
  useEffect(() => {
    setText(search);
  }, [search]);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedText(text);
    }, debounceMs);
    return () => {
      clearTimeout(timer);
    };
  }, [text, debounceMs]);
  useEffect(() => {
    if (debouncedValue !== initialValue) {
      onSearch(debouncedValue);
    }
  }, [debouncedValue, initialValue, onSearch]);
  const jumpToSearch = (event) => {
    logDebug("Received search bar hotkey");
    event.preventDefault();
    if (ref.current != null) {
      logDebug("Invoking search bar");
      ref.current.focus();
    }
  };
  useEffect(() => {
    logDebug("Capturing '/' + 'Shift+/' for jump to search");
    hotkeys("/, shift+/", jumpToSearch);
  }, []);
  const fieldText = text ?? "";
  return /* @__PURE__ */ jsx2(Fragment2, { children: /* @__PURE__ */ jsxs2(Search, { children: [
    /* @__PURE__ */ jsx2(SearchIconWrapper, { children: /* @__PURE__ */ jsx2(SearchIcon, {}) }),
    /* @__PURE__ */ jsx2(
      StyledTextField,
      {
        id: "search",
        inputRef: ref,
        placeholder: "Search\u2026",
        inputProps: { "aria-label": "search" },
        value: fieldText,
        onChange: (e) => {
          if (e.target.value.trim().length > 0) {
            setText(e.target.value);
          } else {
            setText(void 0);
          }
        },
        onKeyDown: (e) => {
          if (e.code === "Escape") {
            setText(void 0);
          }
          if (e.code === "Enter") {
            setDebouncedText(text);
          }
        },
        sx
      }
    )
  ] }) });
};

// lib/appbar/desktop.tsx
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var Toolbar = styled3(MuiToolbar)`
  gap: 0.5rem;
  line-height: 0;

  &.${toolbarClasses.root} {
    padding-left: 1rem;
    padding-right: 1rem;
  }
`;
var ImageContainer = styled3.div`
  line-height: 0;

  & svg {
    color: white;
  }
`;
var Desktop = ({
  name,
  logo,
  versionLogo,
  content,
  setTick,
  searchTerm,
  setSearchTerm,
  buttons = [],
  version,
  loginUrl,
  rootUrl,
  enableSearch = true
}) => {
  return /* @__PURE__ */ jsx3(MuiAppBar, { children: /* @__PURE__ */ jsxs3(Toolbar, { children: [
    /* @__PURE__ */ jsx3(Title, { name, logo, versionLogo }),
    content,
    enableSearch ? /* @__PURE__ */ jsx3(SearchAppBar, { search: searchTerm, onSearch: setSearchTerm }) : null,
    buttons.filter((b) => b.visible ?? true).map((button) => {
      return /* @__PURE__ */ jsx3(
        IconButton,
        {
          onClick: button.onClick,
          disabled: button.disabled,
          sx: button.disabled ? { opacity: 0.5 } : null,
          children: /* @__PURE__ */ jsx3(ImageContainer, { children: button.icon })
        },
        button.id
      );
    }),
    /* @__PURE__ */ jsx3(
      IconButton,
      {
        onClick: () => {
          setTick(Date.now());
        },
        sx: { color: "white" },
        children: /* @__PURE__ */ jsx3(RefreshIcon, {})
      }
    ),
    /* @__PURE__ */ jsx3(UserInfo, { version, loginUrl, rootUrl })
  ] }) });
};

// lib/appbar/mobile.tsx
import styled4 from "@emotion/styled";
import { UserInfo as UserInfo2 } from "@lune/auth/user";
import {
  IconButton as IconButton2,
  AppBar as MuiAppBar2,
  Toolbar as MuiToolbar2,
  toolbarClasses as toolbarClasses2
} from "@mui/material";
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
var Toolbar2 = styled4(MuiToolbar2)`
  gap: 0.5rem;
  line-height: 0;

  &.${toolbarClasses2.root} {
    padding-left: 1rem;
    padding-right: 1rem;
  }
`;
var ImageContainer2 = styled4.div`
  line-height: 0;

  & svg {
    color: white;
    width: 24px;
    height: 24px;
  }
`;
var Mobile = ({
  name,
  logo,
  content,
  searchTerm,
  setSearchTerm,
  buttons = [],
  version,
  loginUrl,
  rootUrl,
  enableSearch = true
}) => /* @__PURE__ */ jsx4(MuiAppBar2, { children: /* @__PURE__ */ jsxs4(Toolbar2, { children: [
  /* @__PURE__ */ jsx4(Title, { name, logo }),
  content,
  enableSearch ? /* @__PURE__ */ jsx4(SearchAppBar, { search: searchTerm, onSearch: setSearchTerm }) : null,
  buttons.filter((b) => b.visible ?? true).map((button) => /* @__PURE__ */ jsx4(
    IconButton2,
    {
      onClick: button.onClick,
      disabled: button.disabled,
      sx: button.disabled ? { opacity: 0.5 } : null,
      children: /* @__PURE__ */ jsx4(ImageContainer2, { children: button.icon })
    },
    button.id
  )),
  /* @__PURE__ */ jsx4(UserInfo2, { version, loginUrl, rootUrl })
] }) });

// lib/appbar/appbar.tsx
import { Fragment as Fragment3, jsx as jsx5 } from "react/jsx-runtime";
var AppBar = (props) => /* @__PURE__ */ jsx5(Fragment3, { children: isMobile() ? /* @__PURE__ */ jsx5(Mobile, { ...props }) : /* @__PURE__ */ jsx5(Desktop, { ...props }) });

export {
  SearchAppBar,
  AppBar
};
